import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Form, Spinner, Alert } from "reactstrap";
import Cookies from 'js-cookie';

import LogoDark2x from "../../images/logo-dark2x.png";
import LogoLight2x from "../../images/logo-light2x.png";
import Head from "../../layout/head/Head";
import { useNavigate } from 'react-router-dom';
import { Icon } from "../../components/Component";

import { LoginCredentials } from "../../api/types/auth/authTypes";
import { login } from "../../api/auth/authService";

const Login: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [passState, setPassState] = useState<boolean>(false);
  const [errorVal, setError] = useState<string>("");

  const { register, handleSubmit, formState: { errors } } = useForm<LoginCredentials>();
  const navigate = useNavigate();

  const onFormSubmit = async (formData: LoginCredentials) => {
    setLoading(true);
    setError("");
    try {
      const data: any = await login({ ...formData, applicationType: "admin" });
      if (data?.status && data?.token && data?.token?.access_token) {
        Cookies.set('access-token', JSON.stringify(data.token), { expires: 7, secure: true, sameSite: 'Strict' });
        Cookies.set('refresh-token', data.token.refresh_token, { expires: 7, secure: true, sameSite: 'Strict' });
        setTimeout(() => {
          navigate('/party-list');
        }, 1000);
        setLoading(false);
      } else {
        // On rejected credentials the response has no token (and may be undefined),
        // so show a user-friendly message instead of crashing on data.status.
        setError(data?.message || "Invalid username/Password");
        setLoading(false);
      }
    } catch (err: any) {
      console.log('Login failed:', err?.message);
      setError("Unable to login. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {}, []);

  return (
    <>
      <Head title="Login" />
      <div style={styles.wrapper}>
        {/* Left Panel - Branding */}
        <div className="login-left-panel" style={styles.leftPanel}>
          <div style={styles.leftOverlay}>
            {/* Animated background shapes */}
            <div style={styles.shape1}></div>
            <div style={styles.shape2}></div>
            <div style={styles.shape3}></div>

            <div style={styles.leftContent}>
              <div style={styles.logoWrapper}>
                <img src={LogoLight2x} alt="ProcureX" style={styles.leftLogo} />
              </div>
              <h1 style={styles.leftTitle}>Admin Portal</h1>
              <p style={styles.leftSubtitle}>
                Manage your procurement operations, billing, and enterprise configurations from one centralized platform.
              </p>

              <div style={styles.featureList}>
                <div style={styles.featureItem}>
                  <div style={styles.featureIcon}>
                    <Icon name="shield-check" />
                  </div>
                  <span>Secure & Reliable</span>
                </div>
                <div style={styles.featureItem}>
                  <div style={styles.featureIcon}>
                    <Icon name="growth" />
                  </div>
                  <span>Real-time Analytics</span>
                </div>
                <div style={styles.featureItem}>
                  <div style={styles.featureIcon}>
                    <Icon name="users" />
                  </div>
                  <span>Multi-tenant Management</span>
                </div>
              </div>
            </div>

            <div style={styles.leftFooter}>
              <p>&copy; {new Date().getFullYear()} ProcureX Technologies Pvt Ltd</p>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="login-right-panel" style={styles.rightPanel}>
          <div style={styles.formContainer}>
            {/* Mobile logo (visible only on small screens) */}
            <div className="login-mobile-logo" style={styles.mobileLogo}>
              <img src={LogoDark2x} alt="ProcureX" style={{ height: 40 }} />
            </div>

            <div style={styles.formHeader}>
              <h2 style={styles.welcomeText}>Welcome back</h2>
              <p style={styles.welcomeSub}>Sign in to your admin account to continue</p>
            </div>

            {errorVal && (
              <Alert color="danger" className="alert-icon" style={{ borderRadius: 10, fontSize: 13 }}>
                <Icon name="alert-circle" /> {errorVal || "Unable to login with credentials"}
              </Alert>
            )}

            <Form onSubmit={handleSubmit(onFormSubmit)} style={{ marginTop: 8 }}>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="login-email">
                  Email Address
                </label>
                <div style={styles.inputWrapper}>
                  <span style={styles.inputIcon}>
                    <Icon name="mail" />
                  </span>
                  <input
                    type="text"
                    id="login-email"
                    className="login-input"
                    {...register('email', { required: "Email is required" })}
                    placeholder="name@company.com"
                    style={{
                      ...styles.input,
                      borderColor: errors.email ? "#ff4d4f" : "#e2e8f0",
                    }}
                  />
                </div>
                {errors.email && <span style={styles.errorText}>{errors.email.message}</span>}
              </div>

              <div style={styles.inputGroup}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={styles.label} htmlFor="login-password">
                    Password
                  </label>
                  <Link
                    to={`${process.env.PUBLIC_URL}/auth-reset`}
                    style={styles.forgotLink}
                  >
                    Forgot password?
                  </Link>
                </div>
                <div style={styles.inputWrapper}>
                  <span style={styles.inputIcon}>
                    <Icon name="lock-alt" />
                  </span>
                  <input
                    type={passState ? "text" : "password"}
                    id="login-password"
                    className="login-input"
                    {...register('password', { required: "Password is required" })}
                    placeholder="Enter your password"
                    style={{
                      ...styles.input,
                      borderColor: errors.password ? "#ff4d4f" : "#e2e8f0",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setPassState(!passState)}
                    style={styles.eyeButton}
                  >
                    <Icon name={passState ? "eye-off" : "eye"} />
                  </button>
                </div>
                {errors.password && <span style={styles.errorText}>{errors.password.message}</span>}
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.submitBtn,
                  opacity: loading ? 0.8 : 1,
                }}
              >
                {loading ? (
                  <Spinner size="sm" color="light" />
                ) : (
                  <>
                    Sign In
                    <Icon name="arrow-right" style={{ marginLeft: 8 }} />
                  </>
                )}
              </button>
            </Form>

            <div style={styles.bottomLinks}>
              <span style={{ color: "#94a3b8", fontSize: 13 }}>
                Need help? Contact{" "}
                <a href="mailto:support@procurex.com" style={{ color: "#6576ff", textDecoration: "none" }}>
                  support@procurex.com
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .nk-wrap-nosidebar .nk-content {
          padding: 0 !important;
          min-height: 100vh;
        }
        .nk-wrap-nosidebar {
          min-height: 100vh;
        }
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -50px) rotate(120deg); }
          66% { transform: translate(-20px, 20px) rotate(240deg); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(-40px, 30px) rotate(-120deg); }
          66% { transform: translate(20px, -40px) rotate(-240deg); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(25px, 25px); }
        }
        .login-input:focus {
          border-color: #6576ff !important;
          box-shadow: 0 0 0 3px rgba(101, 118, 255, 0.12) !important;
          outline: none !important;
        }
        .login-input::placeholder {
          color: #cbd5e1;
        }
        button[type="submit"]:hover {
          box-shadow: 0 6px 20px rgba(101, 118, 255, 0.45) !important;
          transform: translateY(-1px);
        }
        button[type="submit"]:active {
          transform: translateY(0);
        }
        @media (max-width: 900px) {
          .login-left-panel { display: none !important; }
          .login-right-panel { width: 100% !important; }
          .login-mobile-logo { display: block !important; }
        }
      `}</style>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: "flex",
    minHeight: "100vh",
    width: "100%",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
  },
  // Left Panel
  leftPanel: {
    width: "45%",
    background: "linear-gradient(135deg, #101924 0%, #1f2b3a 50%, #101924 100%)",
    position: "relative",
    overflow: "hidden",
  },
  leftOverlay: {
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "60px 50px",
    zIndex: 2,
  },
  shape1: {
    position: "absolute",
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(101, 118, 255, 0.15) 0%, transparent 70%)",
    top: "10%",
    right: "-10%",
    animation: "float1 20s ease-in-out infinite",
    zIndex: 0,
  },
  shape2: {
    position: "absolute",
    width: 200,
    height: 200,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(101, 118, 255, 0.2) 0%, transparent 70%)",
    bottom: "15%",
    left: "-5%",
    animation: "float2 15s ease-in-out infinite",
    zIndex: 0,
  },
  shape3: {
    position: "absolute",
    width: 150,
    height: 150,
    borderRadius: "30%",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    top: "60%",
    right: "20%",
    animation: "float3 12s ease-in-out infinite",
    zIndex: 0,
  },
  leftContent: {
    textAlign: "center",
    zIndex: 2,
    maxWidth: 400,
  },
  logoWrapper: {
    marginBottom: 32,
  },
  leftLogo: {
    height: 50,
  },
  leftTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 16,
    letterSpacing: "-0.5px",
  },
  leftSubtitle: {
    fontSize: 15,
    color: "rgba(255, 255, 255, 0.65)",
    lineHeight: 1.7,
    marginBottom: 40,
  },
  featureList: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    alignItems: "flex-start",
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: 260,
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: 14,
    fontWeight: 500,
  },
  featureIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "rgba(101, 118, 255, 0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#a5b4fc",
    fontSize: 16,
    flexShrink: 0,
  },
  leftFooter: {
    position: "absolute",
    bottom: 30,
    color: "rgba(255, 255, 255, 0.3)",
    fontSize: 12,
    zIndex: 2,
  },
  // Right Panel
  rightPanel: {
    width: "55%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8fafc",
    overflowY: "auto",
  },
  formContainer: {
    width: "100%",
    maxWidth: 420,
    padding: "40px 32px",
  },
  mobileLogo: {
    display: "none",
    textAlign: "center" as const,
    marginBottom: 32,
  },
  formHeader: {
    marginBottom: 32,
  },
  welcomeText: {
    fontSize: 28,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 8,
    letterSpacing: "-0.5px",
  },
  welcomeSub: {
    fontSize: 15,
    color: "#94a3b8",
    margin: 0,
  },
  inputGroup: {
    marginBottom: 22,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#475569",
    marginBottom: 8,
    letterSpacing: "0.3px",
  },
  inputWrapper: {
    position: "relative" as const,
  },
  inputIcon: {
    position: "absolute" as const,
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#94a3b8",
    fontSize: 16,
    pointerEvents: "none" as const,
    zIndex: 1,
  },
  input: {
    width: "100%",
    height: 48,
    padding: "0 44px",
    fontSize: 14,
    color: "#1e293b",
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    outline: "none",
    transition: "all 0.2s ease",
    background: "#ffffff",
    boxSizing: "border-box" as const,
  },
  eyeButton: {
    position: "absolute" as const,
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    padding: 4,
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    zIndex: 1,
  },
  errorText: {
    fontSize: 12,
    color: "#ff4d4f",
    marginTop: 4,
    display: "block",
  },
  forgotLink: {
    fontSize: 12,
    color: "#6576ff",
    textDecoration: "none",
    fontWeight: 500,
  },
  submitBtn: {
    width: "100%",
    height: 50,
    background: "linear-gradient(135deg, #6576ff 0%, #5a4fcf 100%)",
    color: "#ffffff",
    border: "none",
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    marginTop: 8,
    boxShadow: "0 4px 14px rgba(101, 118, 255, 0.35)",
    letterSpacing: "0.3px",
  },
  bottomLinks: {
    textAlign: "center" as const,
    marginTop: 32,
    paddingTop: 24,
    borderTop: "1px solid #e2e8f0",
  },
};

export default Login;
